import cx from 'classnames';

import Collapsible from 'modules/Collapsible/Collapsible';
import useRoomAvatar from 'modules/Workspaces/hooks/useRoomAvatar';
import useWorkspacesContext from 'modules/Workspaces/WorkspacesProvider/useWorkspacesContext';

import WorkspaceItemRow from './WorkspaceItemRow';
import { WorkspaceSubTreeLines } from './WorkspaceSubTree';
import { WorkspaceEntity } from '../utils';
import { isAncestorOfSelected, shouldExpandForSelection } from '../workspaceTreeUtils';

interface WorkspacesItemProps {
  workspace: WorkspaceEntity;
  selectedId: string | null;
  depth: number;
  className?: string;
  selectWorkspace?: (id: string) => void;
  hasMemberCount?: boolean;
  initiallyExpandedDepth?: number;
  expandSelectedPath?: boolean;
}

const WorkspaceItem = ({
  workspace,
  selectedId,
  depth = 0,
  selectWorkspace,
  hasMemberCount = false,
  initiallyExpandedDepth = 0,
  expandSelectedPath = false,
}: WorkspacesItemProps) => {
  const { workspacesState } = useWorkspacesContext();
  const { name, childWorkspaces, id, memberCount } = workspace;
  const isSelected = selectedId === id;
  const hasChildren = childWorkspaces.length > 0;
  const isPreviewMode = !selectedId && !selectWorkspace;
  const { avatarUrl } = useRoomAvatar({ roomId: id });

  const handleWorkspaceSelect = selectWorkspace ? () => selectWorkspace(id) : undefined;

  const shouldExpand =
    expandSelectedPath && hasChildren
      ? shouldExpandForSelection(id, selectedId, workspacesState.workspaces)
      : depth < initiallyExpandedDepth;

  const isChevronActive =
    expandSelectedPath &&
    hasChildren &&
    (isSelected || isAncestorOfSelected(id, selectedId, workspacesState.workspaces));

  return (
    <Collapsible isCollapsible isInitiallyCollapsed={!shouldExpand}>
      <WorkspaceItemRow
        workspaceId={id}
        name={name}
        depth={depth}
        onSelect={handleWorkspaceSelect}
        isSelected={isSelected}
        hasChildren={hasChildren}
        hasMemberCount={hasMemberCount}
        memberCount={memberCount}
        avatarUrl={avatarUrl}
        isChevronActive={isChevronActive}
        className={cx({ 'justify-between': !isPreviewMode })}
      />
      <Collapsible.Content className="!p-0 !space-y-0" containerClassName="!space-y-0" hasDivider={false}>
        <WorkspaceSubTreeLines.Root childCount={childWorkspaces.length}>
          {childWorkspaces.map((workspaceId, index) => {
            const isLast = index === childWorkspaces.length - 1;
            const childWorkspace = workspacesState.workspaces[workspaceId];

            if (!childWorkspace) return null;

            return (
              <WorkspaceSubTreeLines.Item key={workspaceId} isLast={isLast}>
                <WorkspaceItem
                  workspace={childWorkspace}
                  selectedId={selectedId}
                  depth={depth + 1}
                  className="!mt-0 !p-0"
                  selectWorkspace={selectWorkspace}
                  initiallyExpandedDepth={initiallyExpandedDepth}
                  expandSelectedPath={expandSelectedPath}
                  hasMemberCount={hasMemberCount}
                />
              </WorkspaceSubTreeLines.Item>
            );
          })}
        </WorkspaceSubTreeLines.Root>
      </Collapsible.Content>
    </Collapsible>
  );
};

export default WorkspaceItem;
